import { useEffect, useState, useRef } from 'react'
import { useDebounceCallback, useResizeObserver } from '@buffer-mono/popcorn'

export type Measurements = {
  itemWidths: number[]
  overflowBadge: number
  gap: number
}

export interface UseVisibleElementsReturn {
  containerRef: React.RefObject<HTMLDivElement>
  measurementRef: React.RefObject<HTMLDivElement>
  visibleCount: number
  hiddenCount: number
}

/**
 * This hook calculates how many elements can fit in a container's width before needing to show an overflow indicator.
 *
 * ## How it works:
 * The hook uses a "dual-container" approach:
 * 1. **Measurement container** (`measurementRef`): A hidden container that renders ALL items to measure their actual widths
 * 2. **Display container** (`containerRef`): The visible container whose width determines the available space
 * 3. **Calculation**: Based on the measurements, it calculates how many items fit and how many are hidden
 *
 * ## Important Notes:
 * - Only works with horizontal layouts (width-based calculations)
 * - Expects the last child in the measurement container to be an "overflow badge" (e.g., "+3" button)
 * - Uses debounced resize observations for performance
 * - Measurement container should be visually hidden but not `display: none`
 *
 * @param {Object} options - Configuration options
 * @param {number} [options.minimumVisibleCount=0] - Minimum items required before calculations begin.
 *   If total items ≤ this number, returns visibleCount=0 and hiddenCount=0
 *
 * @returns {UseVisibleElementsReturn} Object containing refs and calculated counts
 *
 * @example
 * const { containerRef, measurementRef, visibleCount, hiddenCount } = useVisibleElements({
 *   minimumVisibleCount: 1
 * })
 *
 * const visibleChannels = channels.slice(0, visibleCount)
 *
 * // Hidden measurement container - renders ALL items for width calculation
 * <div ref={measurementRef} style={{ position: 'absolute', visibility: 'hidden' }}>
 *   {channels.map((channel) => (
 *     <ChannelItem key={`measure-${channel.id}`} channel={channel} />
 *   ))}
 *   <Button>+{hiddenCount}</Button> // Overflow badge
 * </div>
 *
 * // Visible container - renders only items that fit
 * <div ref={containerRef}>
 *   {visibleChannels.map((channel) => (
 *     <ChannelItem key={channel.id} channel={channel} />
 *   ))}
 *   {hiddenCount > 0 && (
 *     <Button onClick={showMore}>+{hiddenCount}</Button>
 *   )}
 * </div>
 */
export function useVisibleElements({
  minimumVisibleCount = 0,
}: {
  minimumVisibleCount?: number
}): UseVisibleElementsReturn {
  const [visibleCount, setVisibleCount] = useState(0)
  const [hiddenCount, setHiddenCount] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const measurementRef = useRef<HTMLDivElement>(null)

  const calculate = useDebounceCallback((): void => {
    if (!containerRef.current || !measurementRef.current) {
      return
    }
    const containerWidth = containerRef.current?.getBoundingClientRect().width
    const measurementWidth =
      measurementRef.current?.getBoundingClientRect().width ?? 0
    const children = Array.from(measurementRef.current?.childNodes ?? [])

    if (children.length <= minimumVisibleCount) {
      setVisibleCount(0)
      setHiddenCount(0)
      return
    }

    const childrenWidths = children.map((child) => {
      if (child instanceof HTMLElement) {
        return child.getBoundingClientRect().width
      }
      return 0
    })

    const sumOfAllChildrenWidths = childrenWidths.reduce(
      (sum, width) => sum + width,
      0,
    )

    const gap = Math.max(
      0,
      (measurementWidth - sumOfAllChildrenWidths) / children.length,
    )

    // Get the item widths (excluding overflow badge)
    const mainItemWidths = childrenWidths.slice(0, -1)
    const overflowBadgeWidth = childrenWidths[childrenWidths.length - 1]

    // Calculate total width of all items
    const totalItemsWidth = mainItemWidths.reduce(
      (sum, width) => sum + width,
      0,
    )

    const numGapsBetweenItems = mainItemWidths.length - 1

    const totalWidthWithGap = totalItemsWidth + numGapsBetweenItems * gap

    if (totalWidthWithGap <= containerWidth) {
      setVisibleCount(mainItemWidths.length)
      setHiddenCount(0)
      return
    }

    let sumOfVisibleItemsWidth = 0
    let visibleCount = 0

    const availableWidthWithButton = containerWidth - (overflowBadgeWidth + gap)

    let currentItemIndex = 0
    while (sumOfVisibleItemsWidth <= availableWidthWithButton) {
      const itemWidth = mainItemWidths[currentItemIndex]
      sumOfVisibleItemsWidth += (currentItemIndex === 0 ? 0 : gap) + itemWidth
      currentItemIndex++
    }
    visibleCount = currentItemIndex - 1

    const result = {
      visibleCount: Math.max(0, visibleCount), // Ensure non-negative
      hiddenCount: Math.max(0, mainItemWidths.length - visibleCount),
    }

    setVisibleCount(result.visibleCount)
    setHiddenCount(result.hiddenCount)
  }, 100)

  useResizeObserver<HTMLDivElement>({
    ref: containerRef,
    onResize: calculate,
  })
  useResizeObserver<HTMLDivElement>({
    ref: measurementRef,
    onResize: calculate,
  })

  useEffect(() => {
    return (): void => calculate.cancel()
  }, [calculate])

  return {
    containerRef,
    measurementRef,
    visibleCount,
    hiddenCount,
  }
}
